import { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const captchaRef = useRef(null);

  // 生成验证码
  const generateCaptcha = () => {
    const canvas = captchaRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // 清空画布
    ctx.clearRect(0, 0, width, height);
    
    // 生成背景
    ctx.fillStyle = '#f0f2f5';
    ctx.fillRect(0, 0, width, height);
    
    // 生成随机验证码
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let captcha = '';
    for (let i = 0; i < 4; i++) {
      captcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // 保存验证码到 localStorage
    sessionStorage.setItem('captcha', captcha.toLowerCase());
    
    // 绘制验证码
    ctx.font = 'bold 24px Arial';
    ctx.textBaseline = 'middle';
    const startX = 25;
    const spacing = 25;

    for (let i = 0; i < captcha.length; i++) {
      ctx.save();
      // 随机位置
      const x = startX + i * spacing;
      const y = height / 2;

      // 随机旋转
      const angle = (Math.random() - 0.5) * 0.4;
      ctx.translate(x, y);
      ctx.rotate(angle);
      
      // 随机颜色
      const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'];
      ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
      
      ctx.fillText(captcha[i], 0, 0);
      ctx.restore();
    }
    
    // 添加干扰线
    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.strokeStyle = `rgba(0, 0, 0, ${Math.random() * 0.3})`;
      ctx.stroke();
    }
    
    // 添加干扰点
    for (let i = 0; i < 50; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, 1, 0, 2 * Math.PI);
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.3})`;
      ctx.fill();
    }
  };

  // 刷新验证码
  const refreshCaptcha = () => {
    generateCaptcha();
  };

  // 组件加载时生成验证码
  useEffect(() => {
    generateCaptcha();
  }, []);

  const [loginError, setLoginError] = useState('');

  const onFinish = async (values) => {
    setLoginError('');

    // 从 DOM 直接获取验证码输入值
    const captchaInput = document.getElementById('captcha-input');
    const captchaValue = captchaInput ? captchaInput.value : '';

    // 验证验证码
    const storedCaptcha = sessionStorage.getItem('captcha');

    if (!captchaValue || captchaValue.trim() === '') {
      setLoginError('请输入验证码');
      return;
    }

    const inputLower = captchaValue.toLowerCase().trim();

    if (inputLower !== storedCaptcha) {
      setLoginError('验证码错误');
      refreshCaptcha();
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.login(values.username, values.password);

      if (response.code === 0 && response.data && response.data.token) {
        const token = response.data.token;
        localStorage.setItem('admin_token', token);
        message.success('登录成功');
        window.location.href = '/';
      } else {
        const errMsg = response.message || response.msg || response.error || '登录失败';
        setLoginError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
        refreshCaptcha();
      }
    } catch (error) {
      let errMsg = '';
      const d = error?.response?.data || error;
      if (typeof d === 'string') errMsg = d;
      else errMsg = d?.message || d?.msg || d?.error || '登录失败，请检查账号和密码';
      errMsg = String(errMsg);
      setLoginError(errMsg);
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">Blue API</h1>
        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入账号' },
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="管理员账号" 
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '24px' }}>
            <div style={{ flex: 1 }}>
              <Input
                prefix={<SafetyOutlined />}
                placeholder="验证码"
                maxLength={4}
                id="captcha-input"
                name="captcha"
                onChange={(e) => {
                  console.log('captcha 输入变化:', e.target.value);
                }}
              />
            </div>
            <canvas
              ref={captchaRef}
              width={120}
              height={40}
              style={{ cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: '6px' }}
              onClick={refreshCaptcha}
              title="点击刷新验证码"
            />
          </div>

          {loginError && (
            <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#cf1322', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>⚠</span> {loginError}
            </div>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              登录
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Login;
